import React, { type MouseEventHandler, useState } from 'react';
import { Box } from '@mui/material';
import { type ClassNameMap } from '@mui/styles';
import routes from '../../helpers/routes';
import { type CountryType } from '../../data/location/countries';
import { getStateList, type StateType } from '../../data/location/states';
import { type TopStatesResult } from '../../services/states';
import { findStateSVGData } from '../../data/location/state-images';
import { useStyles } from './CountryMap.styles';
import window from '../../globalWrappers/window';

interface MapDetailsProps {
  classes: ClassNameMap;
  stateName: string;
  businessesCount: number;
}

const MapDetails = ({
  classes,
  stateName,
  businessesCount,
}: MapDetailsProps) => {
  return (
    <Box className={classes.detailsBox}>
      <Box className={classes.detailsBoxName}>{stateName}</Box>
      <Box className={classes.detailsBoxCount}>{businessesCount}</Box>
    </Box>
  );
};

interface MapStateProps {
  dimensions: string;
  name: string;
  businessesCount: string;
  onClickState: MouseEventHandler;
  classes: ClassNameMap;
}

const MapState = ({
  dimensions,
  name,
  businessesCount,
  onClickState,
  classes,
}: MapStateProps) => {
  return (
    <path
      d={dimensions}
      data-state-name={name}
      data-businesses-count={businessesCount}
      className={classes.stateArea}
      onClick={onClickState}
      suppressHydrationWarning
    />
  );
};

interface CountryMapProps {
  country: CountryType;
  states: TopStatesResult[];
  projectsSearch?: boolean;
}

const CountryMap = ({
  country,
  states,
  projectsSearch = false,
}: CountryMapProps) => {
  const emptyMapDetailsProps = {
    visible: false,
    top: '0',
    left: '0',
    stateName: '',
    businessesCount: 0,
  };
  const [clickedState, setClickedState] = useState(false);
  const [mapDetailsProps, setMapDetailsProps] = useState(emptyMapDetailsProps);
  const classes = useStyles({
    clickedState,
    mapDetailsTop: mapDetailsProps.top,
    mapDetailsLeft: mapDetailsProps.left,
  });

  const handleMouseOver = (event) => {
    if (event.target.tagName === 'path') {
      setMapDetailsProps((currentMapDetailsProps) => ({
        ...currentMapDetailsProps,
        visible: true,
        stateName: event.target.dataset.stateName,
        businessesCount: event.target.dataset.businessesCount,
      }));
    } else {
      setMapDetailsProps(emptyMapDetailsProps);
    }
  };

  const handleMouseLeave = () => {
    setMapDetailsProps(emptyMapDetailsProps);
  };

  const handleMouseMove = (event) => {
    const x = event.clientX;
    const y = event.clientY;
    setMapDetailsProps((currentMapDetailsProps) => ({
      ...currentMapDetailsProps,
      top: `${y + 20}px`,
      left: `${x}px`,
    }));
  };

  const handleStateClick = (state: StateType) => {
    if (!clickedState) {
      setClickedState(true);
      window.location.href = routes[
        projectsSearch ? 'projectsLocation' : 'location'
      ](country.alpha2, state.abbreviation);
    }
  };

  return (
    <Box className={classes.mapCard}>
      <Box className={classes.mapArea}>
        {mapDetailsProps.visible && (
          <MapDetails
            data-test-id="map-details"
            classes={classes}
            stateName={mapDetailsProps.stateName}
            businessesCount={mapDetailsProps.businessesCount}
          />
        )}
        <svg
          data-test-id="map-svg"
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="39vw"
          viewBox={
            country.alpha2 === 'CA' ? '900 330 1344 950' : '0 0 1120 570'
          }
          style={{
            transform: 'scale(1.00)',
            maxHeight: '580px',
          }}
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
        >
          <g>
            {getStateList(country).map((state) => (
              <MapState
                data-test-id="map-state"
                data-test-id-item={`map-state-${state.abbreviation}`}
                key={state.abbreviation}
                dimensions={findStateSVGData(country, state)}
                name={state.name}
                businessesCount={states
                  .find((result) => result.state === state.abbreviation)
                  ?.count?.toLocaleString()}
                onClickState={() => handleStateClick(state)}
                classes={classes}
              />
            ))}
          </g>
        </svg>
      </Box>
    </Box>
  );
};

export default CountryMap;
